import _ from 'lodash';

import type { TableRow } from '@/components/ui/table/table.type';
import type {
  DATA_EDITOR_ADD_ROW,
  DATA_EDITOR_BATCH_UPDATE,
  DATA_EDITOR_DELETE_ROWS,
  DATA_EDITOR_SET,
  DATA_EDITOR_UNSET,
  DATA_EDITOR_UPDATE,
  DataEditorActionType
} from '@/constant/dataEditor';
import { executeStringFn } from '@/utils/executeFn';
import { getByPath, setAtPath } from '@/utils/widgets';

export type DataEditorAction<
  T extends DataEditorActionType | undefined = undefined
> = (
  | {
      type: typeof DATA_EDITOR_SET;
      modifiedData: TableRow[] | undefined;
    }
  | {
      type: typeof DATA_EDITOR_UNSET;
    }
  | {
      type: typeof DATA_EDITOR_UPDATE;
      rowSelector: string | number;
      dataToChange: { path: string; value: any };
    }
  | {
      type: typeof DATA_EDITOR_BATCH_UPDATE;
      rowSelectors: (string | number)[];
      patch: object;
      editablePaths: string[];
    }
  | {
      type: typeof DATA_EDITOR_ADD_ROW;
      newRow: TableRow;
    }
  | {
      type: typeof DATA_EDITOR_DELETE_ROWS;
      rowSelectors: (string | number)[];
    }
) &
  (T extends undefined ? object : { type: T });

export const applyModifiedDataChangeInPlace = (
  setData: React.Dispatch<React.SetStateAction<TableRow[]>>,
  dataSelectorPath: string,
  action: DataEditorAction,
  dataHook?: string
) => {
  /**
   * Computes the data using the provided dataHook function (if any).
   */
  const computeNewData = (data: TableRow[]) => {
    if (dataHook) {
      const computedData = executeStringFn(data, dataHook);
      if (computedData) return computedData;
    }
    return data;
  };

  switch (action.type) {
    case 'set':
      setData(() => {
        const newData = action.modifiedData;
        return computeNewData(newData || []);
      });
      break;
    case 'unset':
      setData(() => {
        const newData = [];
        return computeNewData(newData);
      });
      break;
    case 'update':
      setData((initialData: TableRow[]) => {
        const newData = initialData.map(row => {
          if (getByPath(row, dataSelectorPath) === action.rowSelector) {
            // clone row deeply to avoid touching frozen objects
            const updatedRow = _.cloneDeep(row);
            setAtPath(
              updatedRow,
              action.dataToChange.path,
              action.dataToChange.value
            );
            return updatedRow;
          }
          return row;
        });

        return computeNewData(newData);
      });
      break;
    case 'batch_update':
      setData(initialData => {
        const newData = initialData.map(row => {
          const value = getByPath(row, dataSelectorPath);
          if (value !== undefined && action.rowSelectors.includes(value)) {
            return applyPatches(row, action);
          }
          return row;
        });

        return computeNewData(newData);
      });

      break;
    case 'add_row':
      setData(initialData => {
        const newData = [...initialData, action.newRow];
        return computeNewData(newData);
      });
      break;
    case 'delete_rows':
      setData(initialData => {
        const newData = initialData.filter(row => {
          const value = getByPath(row, dataSelectorPath);
          return value === undefined || !action.rowSelectors.includes(value);
        });
        return computeNewData(newData);
      });
      break;
    default:
      // @ts-expect-error -- exhaustive switch
      throw new Error(`Unknown data reducer type ${action.type}`);
  }
};

const applyPatches = (
  row: TableRow,
  action: DataEditorAction<'batch_update'>
): TableRow => {
  const updated = _.cloneDeep(row);

  action.editablePaths.forEach(path => {
    const patchData = getByPath(action.patch, path);
    if (patchData !== undefined) {
      setAtPath(updated, path, patchData);
    }
  });
  return updated;
};
